import { capitalize, compact, isEmpty, omit, pick, some, trim } from "lodash"
import PropTypes from "prop-types"
import { LONG, LONG_SHORT, SHORT } from "resources/position-sets/composition"
import { clampDate, getPreviousModeledDate } from "helpers/dates"
import { DEFAULT_CURRENCY } from "helpers/formatters/numbers"
import { computeFilterChips, FILTER_TYPES } from "helpers/filters/chips"
import { filterSupportsContribution, removeUnsupportedBaseFilters } from "helpers/segments"
import { computePickerFilterFromSegment, isBaseExperimentOfColumnPortfolio } from "./helpers/segment-filters"

export const PORTFOLIO = "PORTFOLIO"
export const BASKET = "BASKET"
export const SWAP = "SWAP"
export const SECURITY = "SECURITY"
export const ETF = "ETF"
export const INDEX = "INDEX"
export const BENCHMARK = "BENCHMARK"
export const RULES = "RULES"
export const NOT_FOUND_LABEL = "Not Found"

export const CONTRIBUTION = "CONTRIBUTION"
export const STANDALONE = "STANDALONE"

function getPositionSet(rawPositionSet, segment, date, weight, fxHedgeEnabled) {
  if (!rawPositionSet) {
    return undefined
  }

  switch (rawPositionSet.type) {
    case BASKET:
      return { dates: [{ date, swaps: [{ id: rawPositionSet.id, economicExposure: weight }] }], expand: true, segment, fxHedgeEnabled }
    case SECURITY:
      return { dates: [{ date, equities: [{ id: { internalId: rawPositionSet.id }, economicExposure: weight }] }], segment, fxHedgeEnabled }
    case ETF:
      return { dates: [{ date, equities: [{ id: { internalId: rawPositionSet.id }, economicExposure: weight }] }], expand: true, segment, fxHedgeEnabled }
    case INDEX:
      return { dates: [{ date, indexes: [{ id: rawPositionSet.id, economicExposure: weight }] }], expand: true, segment, fxHedgeEnabled }
    case RULES:
      return { type: rawPositionSet.type, rulesConfiguration: rawPositionSet.rulesConfiguration, segment, fxHedgeEnabled }
    default:
      return { type: rawPositionSet.type, id: rawPositionSet.id, segment, fxHedgeEnabled }
  }
}

// Needs metadata this parser has no access to - fall back to the filter type's generic name.
const METADATA_DEPENDENT_FILTER_FIELDS = ["classification", "tag", "positionAttributes"]

function getBaseSegment(segment, canKeepSegment) {
  const filters = canKeepSegment ? segment?.filters : removeUnsupportedBaseFilters(segment?.filters)
  return isEmpty(filters) ? undefined : { ...segment, filters }
}

function getBasePositionSet(rawBase, segment, weight, canKeepSegment) {
  if (!rawBase) {
    return undefined
  }

  return { ...pick(rawBase, ["type", "id", "experimentId"]), segment: getBaseSegment(segment, canKeepSegment), weight }
}

function getFxHedgeEnabled(rawPositionSet, metadataFxHedgeDefault) {
  if (rawPositionSet.fxHedgeEnabled !== undefined) {
    return rawPositionSet.fxHedgeEnabled
  }

  return metadataFxHedgeDefault ?? false
}

export function getIsFxHedgeSupportedByModel(modelFactors, currency) {
  if (!modelFactors?.length) {
    return false
  }
  const effectiveCurrency = currency || DEFAULT_CURRENCY
  return some(modelFactors, factor => factor.category?.toLowerCase() === "currency" && factor.id === effectiveCurrency)
}

function getMetadataDependentFilterLabel(field) {
  return FILTER_TYPES.find(filterType => filterType.field === field)?.label
}

function formatChipLabel(chip) {
  if (!chip.valueLabels) {
    return chip.label
  }
  return `${chip.label} [${chip.valueLabels.map(({ value }) => value).join(", ")}]`
}

export function isLongShortOnlySegment(filters) {
  if (!filters) {
    return false
  }
  const keys = Object.keys(filters)
  return keys.length === 1 && keys[0] === "longShort"
}

export function computeSegmentHeaderLabel(filters, normalization, currency, metadata = {}) {
  if (!filters) {
    return null
  }

  if (isLongShortOnlySegment(filters)) {
    return `${capitalize(filters.longShort)} Only - Normalized as ${capitalize(normalization)}`
  }

  const { tagCategories, classificationsMetas, classificationsValues, attributeCategories, metadataReady = {} } = metadata
  const notReadyFields = METADATA_DEPENDENT_FILTER_FIELDS.filter(field => !metadataReady[field])

  const resolvedFilters = omit(computePickerFilterFromSegment(filters), notReadyFields)
  const resolvedLabels = computeFilterChips(resolvedFilters, tagCategories, classificationsMetas, undefined, undefined, attributeCategories, classificationsValues, currency).map(formatChipLabel)
  const fallbackLabels = compact(notReadyFields.map(field => filters[field] && getMetadataDependentFilterLabel(field)))

  const label = [...resolvedLabels, ...fallbackLabels].join(", ") || null
  return label && `${label} - Normalized as ${capitalize(normalization)}`
}

function getIsColumnActive(fixed, rawBase, isContextActive) {
  if (fixed) {
    return isContextActive
  }
  if (rawBase === false) {
    return false
  }
  if (rawBase) {
    return true
  }
  return isContextActive
}

export function parsePositionSet(rawPositionSet, positionSetWithBase, positionSetMetadata, baseMetadata, date, fixed, isContextActive, isPerformance, modelMetadata, currency, pagePortfolio = null) {
  const evaluatedAsShort = rawPositionSet.asShort || false
  const isActive = getIsColumnActive(fixed, rawPositionSet.base, isContextActive)
  const segmentNormalization = rawPositionSet?.segment?.normalization || null
  const rawBase = positionSetWithBase?.base
  const isExperimentOfOwnPortfolio = isActive && isBaseExperimentOfColumnPortfolio(rawBase, rawPositionSet.type, rawPositionSet.id, pagePortfolio)
  // Contribution never carries tag/attribute filters, whatever the base
  const canKeepSegment = isExperimentOfOwnPortfolio && segmentNormalization === STANDALONE
  const effectiveSegment = isActive ? getBaseSegment(rawPositionSet.segment, canKeepSegment) : rawPositionSet.segment
  const effectiveSegmentFilters = effectiveSegment?.filters || null
  const segmentedBy = effectiveSegmentFilters?.longShort || null
  const segmentHeader = computeSegmentHeaderLabel(effectiveSegmentFilters, segmentNormalization, currency)
  const hasBaseUnsupportedSegment = isActive && !canKeepSegment && !filterSupportsContribution(rawPositionSet.segment?.filters, true)
  const customBenchmark = !fixed && rawPositionSet.base
  const fxHedgeEnabled = getFxHedgeEnabled(rawPositionSet, positionSetMetadata?.fxHedgeEnabled)
  const isFxHedgeSupportedByModel = getIsFxHedgeSupportedByModel(modelMetadata?.factors, currency)
  const effectiveFxHedgeEnabled = fxHedgeEnabled && isFxHedgeSupportedByModel

  const additionalHeaders = compact([
    evaluatedAsShort && "Evaluated as Short",
    segmentHeader,
    isContextActive && !fixed && customBenchmark === false && "Absolute",
    !!customBenchmark && `Active to ${baseMetadata.unavailable ? NOT_FOUND_LABEL : baseMetadata.name}`,
    effectiveFxHedgeEnabled && "FX Hedge Enabled",
  ])

  const weight = rawPositionSet.asShort ? -1 : 1
  let benchmarkWeight = 1
  if (rawPositionSet.asShort || segmentedBy === SHORT || positionSetMetadata.compositionType === SHORT) {
    benchmarkWeight = -1
  }

  let positionSetDate = date
  if (isPerformance) {
    const modelStartDate = modelMetadata?.availability?.securitiesStartDate
    const unmodeledDates = modelMetadata?.availability?.unmodeledDates || []
    const previousModeledDate = getPreviousModeledDate(date, modelStartDate, unmodeledDates)
    positionSetDate = clampDate(previousModeledDate, positionSetMetadata.earliestDate)
  }

  const unavailable = Boolean(positionSetMetadata?.unavailable)

  return {
    name: !unavailable ? (trim(positionSetMetadata.name) || rawPositionSet.id) : "",
    type: rawPositionSet.type,
    fixed,
    unavailable,
    evaluatedAsShort,
    normalization: segmentNormalization,
    longShort: positionSetMetadata.compositionType,
    queryStringPositionSet: rawPositionSet,
    queryPositionSet: getPositionSet(rawPositionSet, effectiveSegment, positionSetDate, weight, effectiveFxHedgeEnabled),
    queryBase: getBasePositionSet(rawBase, rawPositionSet.segment, benchmarkWeight, canKeepSegment),
    additionalHeaders: additionalHeaders.length ? additionalHeaders : null,
    segmentHeader,
    keepsBaseSegment: canKeepSegment,
    isExperimentOfOwnPortfolio,
    effectiveSegmentFilters,
    hasBaseUnsupportedSegment,
    baseName: baseMetadata?.name || null,
    isActive,
    fxHedgeEnabled: effectiveFxHedgeEnabled,
    isFxHedgeSupported: isFxHedgeSupportedByModel,
    referenceEquity: positionSetMetadata.referenceEquity,
  }
}

// Stable per-column React key, with a stable occurrence suffix for duplicate id+type (e.g. "Split into LONG/SHORT").
export function computePositionSetKeys(parsedPositionSets) {
  const occurrencesSeen = new Map()
  return parsedPositionSets.map(positionSet => {
    const identity = `${positionSet.type}:${positionSet.queryStringPositionSet?.id}`
    const occurrence = occurrencesSeen.get(identity) ?? 0
    occurrencesSeen.set(identity, occurrence + 1)
    return occurrence === 0 ? identity : `${identity}:${occurrence}`
  })
}

const QUERY_POSITION_SET = PropTypes.object

export const POSITION_SET = {
  name: PropTypes.string.isRequired,
  type: PropTypes.oneOf([PORTFOLIO, BASKET, SECURITY, ETF, BENCHMARK, INDEX, RULES]).isRequired,
  fixed: PropTypes.bool.isRequired,
  unavailable: PropTypes.bool.isRequired,
  evaluatedAsShort: PropTypes.bool.isRequired,
  normalization: PropTypes.oneOf([CONTRIBUTION, STANDALONE]),
  longShort: PropTypes.oneOf([LONG, SHORT, LONG_SHORT]).isRequired,
  queryStringPositionSet: QUERY_POSITION_SET.isRequired,
  queryPositionSet: QUERY_POSITION_SET.isRequired,
  queryBase: QUERY_POSITION_SET.isRequired,
  additionalHeaders: PropTypes.arrayOf(PropTypes.string),
  segmentHeader: PropTypes.string,
  keepsBaseSegment: PropTypes.bool,
  isExperimentOfOwnPortfolio: PropTypes.bool,
  effectiveSegmentFilters: PropTypes.object,
  hasBaseUnsupportedSegment: PropTypes.bool,
  baseName: PropTypes.string,
  isActive: PropTypes.bool.isRequired,
  fxHedgeEnabled: PropTypes.bool.isRequired,
  isFxHedgeSupported: PropTypes.bool.isRequired,
}
